/*
 * Public API Surface of eventq-components
 */

export * from './lib/primitives/button/button';
export * from './lib/primitives/input/input';
export * from './lib/validators/email.validator';
export * from './lib/validators/required.validator';
export * from './lib/primitives/badge/badge';
export type { EventqBadgeVariant, EventqStatus } from './lib/primitives/badge/badge';
export * from './lib/primitives/boolean-toggle/boolean-toggle';
export * from './lib/primitives/scroll-area/scroll-area';
export type {
  EventqScrollAreaAxis,
  EventqScrollAreaSurface,
} from './lib/primitives/scroll-area/scroll-area';
export * from './lib/primitives/accent-checkbox/accent-checkbox';
export * from './lib/primitives/detection-bounding-box/detection-bounding-box';
export type {
  EventqDetectionBox,
  EventqDetectionBoxClick,
  EventqDetectionBoundingBoxActivate,
  EventqDetectionEventType,
} from './lib/primitives/detection-bounding-box/detection-bounding-box.types';
export { DETECTION_BOUNDING_BOX_ICON_SRC } from './lib/primitives/detection-bounding-box/detection-bounding-box.types';
export * from './lib/composites/action-loader/action-loader';
export * from './lib/composites/alert/alert';
export * from './lib/composites/note-section/note-section';
export * from './lib/composites/toggle-tab/toggle-tab';
export * from './lib/composites/event-meta-bar/event-meta-bar';
export * from './lib/composites/alarm-indicator/alarm-indicator';
export * from './lib/composites/ai-event-tag/ai-event-tag';
export * from './lib/composites/flag-alarm-toggle/flag-alarm-toggle';
export * from './lib/composites/collapsible-search-panel/collapsible-search-panel';
export * from './lib/composites/camera-list-panel/camera-list-panel';
export type { EventqCameraListItem } from './lib/composites/camera-list-panel/camera-list-panel';
export * from './lib/composites/camera-pages/camera-pages';
export { buildCameraPageItems } from './lib/composites/camera-pages/camera-pages';
export * from './lib/composites/camera-grid/camera-grid';
export type {
  EventqCameraGridLayout,
  EventqCameraGridSize,
} from './lib/composites/camera-grid/camera-grid';
export {
  cameraGridCellsPerPage,
  cameraGridDimension,
  cameraGridSize,
  normalizeCameraGridLayout,
} from './lib/composites/camera-grid/camera-grid';
export * from './lib/composites/ptz-controls/ptz-controls';
export type { CameraPtzAction } from './lib/composites/ptz-controls/ptz-controls.types';
export * from './lib/composites/talkdown-group/talkdown-group';
export * from './lib/composites/detection-overlay/detection-overlay';
export type { EventqVideoContentLayout } from './lib/composites/detection-overlay/detection-overlay';
export {
  computeVideoRenderMetrics,
  mapDetectionBoxesToContainer,
  mapVideoBoxToContainerPercent,
} from './lib/utils/video-content-layout.util';
export type {
  EventqPercentRect,
  EventqVideoObjectFit,
  EventqVideoRenderMetrics,
} from './lib/utils/video-content-layout.util';
export * from './lib/composites/mini-playback-controls/mini-playback-controls';
export * from './lib/composites/datetime-picker/datetime-picker';
export type {
  EventqDatetimePickerMode,
  EventqDatetimePickerPanelAlign,
  EventqDatetimePickerPanelPlacement,
} from './lib/composites/datetime-picker/datetime-picker';
export * from './lib/composites/browser-tab/browser-tab';
export * from './lib/composites/browser-tab-bar/browser-tab-bar';
export type { EventqBrowserTabItem } from './lib/composites/browser-tab-bar/browser-tab-bar';
export * from './lib/composites/site-stat-flag/site-stat-flag';
export * from './lib/composites/lined-tab/lined-tab';
export * from './lib/composites/panel-tab/panel-tab';
export * from './lib/composites/detail-field/detail-field';
export * from './lib/composites/site-overview-details/site-overview-details';
export type { EventqSiteOverviewField } from './lib/composites/site-overview-details/site-overview-details';
export * from './lib/composites/site-overview-maps/site-overview-maps';
export type {
  EventqSiteMapImage,
  EventqSiteOverviewMapMode,
} from './lib/composites/site-overview-maps/site-overview-maps';
export * from './lib/composites/site-fields/site-fields';
export * from './lib/composites/schedule-block/schedule-block';
export type { EventqScheduleBlockVariant } from './lib/composites/schedule-block/schedule-block';
export * from './lib/composites/schedule-block-tooltip/schedule-block-tooltip';
export * from './lib/composites/schedule-entry/schedule-entry';
export * from './lib/composites/schedule-list/schedule-list';
export type { EventqScheduleListEntry } from './lib/composites/schedule-list/schedule-list';
export * from './lib/composites/data-table/data-table';
export * from './lib/composites/data-table/data-table.types';
export type {
  EventqShortcutAction,
  EventqShortcutBinding,
  EventqShortcutKeymap,
  EventqShortcutKeymapStore,
  ProvideEventqShortcutsOptions,
} from './lib/shortcuts';
export { provideEventqShortcuts, EventqShortcutScope } from './lib/shortcuts';
export * from './lib/composites/user-bar/user-bar';
export * from './lib/composites/dialog-popover/dialog-popover';
export type { EventqDialogPopoverPlacement } from './lib/composites/dialog-popover/dialog-popover';
export {
  computeAnchoredPopoverPanelStyle,
  parsePopoverPanelWidth,
} from './lib/composites/dialog-popover/dialog-popover-position';
export type { EventqDialogPopoverPanelStyle } from './lib/composites/dialog-popover/dialog-popover-position';
export * from './lib/composites/select/select';
export type {
  EventqSelectMenuPlacement,
  EventqSelectMenuWidth,
  EventqSelectOption,
} from './lib/composites/select/select.types';
export { computeSelectMenuStyle } from './lib/composites/select/select.types';
export * from './lib/composites/file-dropzone/file-dropzone';
export * from './lib/composites/media-thumb-card/media-thumb-card';
export * from './lib/composites/media-enlarge-popover/media-enlarge-popover';
export * from './lib/composites/attachment-card/attachment-card';
export * from './lib/composites/info-card/info-card';
export type {
  EventqInfoCardMeta,
  EventqInfoCardNote,
  EventqInfoCardTone,
} from './lib/composites/info-card/info-card';
export * from './lib/composites/action-form/action-form';
export * from './lib/composites/actionable-list/actionable-list';
export * from './lib/composites/toggle-list/toggle-list';
export type {
  EventqToggleListItem,
  EventqToggleListItemChange,
  EventqToggleListLabelPosition,
  EventqToggleListVariant,
} from './lib/composites/toggle-list/toggle-list';
export { EventqSearchableList } from './lib/composites/searchable-list';
export type {
  EventqSearchableListAppearance,
  EventqSearchableListColumn,
  EventqSearchableListColumnStyle,
  EventqSearchableListColumnType,
  EventqSearchableListRow,
  EventqSearchableListRowAction,
  EventqSearchableListSelectionMode,
} from './lib/composites/searchable-list';
export * from './lib/composites/protocol-action-section/protocol-action-section';
export * from './lib/composites/resolve-submit/resolve-submit';
export * from './lib/composites/split-menu-button/split-menu-button';
export * from './lib/composites/notice/notice';
export * from './lib/composites/site-selection-popover/site-selection-popover';
export type {
  EventqSiteLocation,
  EventqSiteSelectionConfirm,
} from './lib/composites/site-selection-popover/site-selection-popover.types';
export * from './lib/composites/sites-status-list-popover/sites-status-list-popover';
export type {
  SitesStatusListSite,
  SitesStatusListVariant,
} from './lib/composites/sites-status-list-popover/sites-status-list-popover.types';
export * from './lib/composites/arm-site-popover/arm-site-popover';
export * from './lib/composites/disarm-site-popover/disarm-site-popover';
export type {
  DisarmSiteConfirm,
  SiteDisarmFixedDurationMinutes,
  SiteDisarmFixedDurationOption,
  SiteDisarmForOption,
  SiteDisarmReasonOption,
} from './lib/composites/disarm-site-popover/disarm-site-popover.types';
export {
  SITE_DISARM_DATE_TIME_DEFAULT_OFFSET_HOURS,
  SITE_DISARM_DATE_TIME_MAX_HOURS,
  SITE_DISARM_FIXED_DURATION_MAX_MINUTES,
  SITE_DISARM_FIXED_DURATION_OPTIONS,
  SITE_DISARM_FIXED_DURATION_PRESET_MINUTES,
  SITE_DISARM_FOR_OPTIONS,
  createDefaultSiteDisarmUntilAt,
  parseCustomDisarmDurationMinutes,
  resolveDisplayedDisarmFixedDurationMinutes,
} from './lib/composites/disarm-site-popover/disarm-site-popover.types';
export * from './lib/composites/popover-search/popover-search';
export * from './lib/composites/priority-filter-row/priority-filter-row';
export type { EventqPriorityFilterOption } from './lib/composites/priority-filter-row/priority-filter-row';
export * from './lib/composites/event-outcome-list/event-outcome-list';
export * from './lib/composites/raise-event-popover/raise-event-popover';
export * from './lib/composites/close-event-popover/close-event-popover';
export * from './lib/composites/flag-report-popover/flag-report-popover';
export * from './lib/composites/activate-talkdown-popover/activate-talkdown-popover';
export * from './lib/composites/park-event-popover/park-event-popover';
export * from './lib/composites/ai-feedback-popover/ai-feedback-popover';
export * from './lib/composites/auto-process-popover/auto-process-popover';
export * from './lib/composites/view-alarm-popover/view-alarm-popover';
export * from './lib/composites/toast/toast';
export * from './lib/composites/toast/toast.service';
export type {
  EventqToastMessage,
  EventqToastPosition,
  EventqToastShowOptions,
  EventqToastTone,
  EventqToastVariantId,
} from './lib/composites/toast/toast.types';
export {
  EVENTQ_TOAST_DEFAULT_DURATION_MS,
  EVENTQ_TOAST_EXIT_ANIMATION_MS,
  EVENTQ_TOAST_PRESETS,
} from './lib/composites/toast/toast.types';
export type {
  CameraReportConfirm,
  CameraReportReasonId,
  CameraReportReasonOption,
} from './lib/composites/flag-report-popover/flag-report-popover.types';
export { CAMERA_REPORT_REASON_OPTIONS } from './lib/composites/flag-report-popover/flag-report-popover.types';
export type {
  ActivateTalkdownConfirm,
  ActivateTalkdownOption,
  ActivateTalkdownOptionId,
} from './lib/composites/activate-talkdown-popover/activate-talkdown-popover.types';
export { ACTIVATE_TALKDOWN_OPTIONS } from './lib/composites/activate-talkdown-popover/activate-talkdown-popover.types';
export type {
  ParkEventConfirm,
  ParkEventDurationMinutes,
  ParkEventDurationOption,
} from './lib/composites/park-event-popover/park-event-popover.types';
export { PARK_EVENT_DURATION_OPTIONS } from './lib/composites/park-event-popover/park-event-popover.types';
export type {
  EventOutcomeConfirm,
  EventOutcomeGroup,
  EventOutcomeItem,
  EventOutcomePriority,
  EventOutcomePriorityFilter,
} from './lib/composites/event-outcome/event-outcome.types';
export {
  EVENT_OUTCOME_GROUP_BORDER_COLORS,
  EVENT_OUTCOME_GROUP_COLORS,
  EVENT_OUTCOME_PRIORITY_ICONS,
  EVENT_OUTCOME_PRIORITY_ORDER,
  isAllOutcomePrioritiesSelected,
  isOutcomePrioritySelected,
  matchesOutcomePrioritySelection,
  toggleOutcomePrioritySelection,
} from './lib/composites/event-outcome/event-outcome.types';
export * from './lib/composites/resolve-queue-row/resolve-queue-row';
export * from './lib/composites/resolve-queue-row/resolve-queue-skeleton-list';
export { SharedWorkerService } from './lib/services/shared-worker.service';
export { SHARED_WORKER_CONFIG, provideSharedWorker } from './lib/services/provide-shared-worker';
export { AppId, SHARED_WORKER_DEFAULT_URL, WORKSPACE_SNAPSHOT_EVENT } from './lib/models/shared-worker.models';
export type {
  SharedWorkerClientMessage,
  SharedWorkerConfig,
  SharedWorkerServerMessage,
  WorkspaceSnapshotPayload,
} from './lib/models/shared-worker.models';
export { BroadcastService } from './lib/services/broadcast.service';

export { SSEService } from './lib/services/sse.service';
export { SSE_CONFIG, provideSse } from './lib/services/provide-sse';
export type { SseConfig } from './lib/services/provide-sse';
export { computeProcessingElapsedMs, formatRuntimeHhMmSs } from './lib/utils/processing-timer.util';
export { SignalRService } from './lib/services/signalr.service';
export { SIGNALR_CONFIG, provideSignalR } from './lib/services/provide-signalr';
export type { SignalRConfig } from './lib/services/provide-signalr';
export { AppEventTypes } from './lib/models/app-event.models';
export type {
  AppEvent,
  EventProcessingMode,
  PageSyncEvent,
  PageSyncState,
  ProcessingTimerSyncState,
} from './lib/models/app-event.models';
export { prefixUrl, provideBaseHref } from './lib/utils/url.util';
export { PrefixUrlPipe } from './lib/pipes/prefix-url.pipe';
